import React, { useState, useEffect, useMemo, useId } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/* ═══════════════════════════════════════════════════════════════════════════
   PAGE FAQ SECTION
   ────────────────
   Optional per-page (or per-item) FAQ block. Renders NOTHING when no
   questions are configured, so it's opt-in everywhere: an admin adds
   questions and the section appears; leaves it empty and the page is
   untouched.

   Two reasons this exists beyond content:
     • FAQ blocks answer long-tail queries, which is how travel pages pick up
       "can I cancel", "what time is check-in" style traffic.
     • It emits FAQPage JSON-LD, which is what makes Google show the expandable
       Q&A directly in the results listing.

   Heading levels: the section title is an h2 and each question an h3, so the
   block slots under a page's single h1 without skipping a level. On a page
   that has no other h2 this still reads correctly.

   The JSON-LD is injected here (client-side) AND, for pages the server knows
   about, server-side in server/lib/seo.js — the server copy is what crawlers
   see in the initial HTML; this one keeps it correct after SPA navigation.
   A stable `scriptId` prevents the two from ever double-registering.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Strip tags/whitespace — JSON-LD answers must be plain text. */
const plain = (s) => String(s || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

/** Keep only entries that have BOTH a question and an answer. A half-filled
 *  row would render an empty accordion and produce invalid schema. */
export const usableFaqs = (faqs) =>
    (Array.isArray(faqs) ? faqs : [])
        .map((f) => ({ q: plain(f?.q), a: plain(f?.a) }))
        .filter((f) => f.q && f.a);

const PageFaqSection = ({
    faqs,
    title,
    subtitle,
    /** Set false on pages where the server already emits FAQPage for this
     *  exact list, to avoid two identical blocks in one document. */
    emitSchema = true,
    className = '',
}) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(null);
    const uid = useId();

    const items = useMemo(() => usableFaqs(faqs), [faqs]);

    // FAQPage JSON-LD — added on mount, removed on unmount so a client-side
    // route change can't leave the previous page's Q&A behind.
    useEffect(() => {
        if (!emitSchema || items.length === 0) return undefined;
        const id = `hm-faq-jsonld-${uid.replace(/[^a-zA-Z0-9]/g, '')}`;
        // A server-rendered FAQPage for this page already covers crawlers.
        if (document.querySelector('script[data-hm-faq="server"]')) return undefined;
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.id = id;
        el.text = JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: items.map((f) => ({
                '@type': 'Question',
                name: f.q,
                acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
        });
        document.head.appendChild(el);
        return () => { const n = document.getElementById(id); if (n) n.remove(); };
    }, [items, emitSchema, uid]);

    if (items.length === 0) return null;

    return (
        <section className={`py-8 md:py-16 ${className}`} aria-labelledby={`${uid}-faq-heading`}>
            <div className="max-w-3xl mx-auto px-4 sm:px-6">
                <div className="text-center mb-8">
                    <span className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-primary mb-2">
                        <HelpCircle size={13} /> {t('faqSection.eyebrow', 'Good to know')}
                    </span>
                    <h2 id={`${uid}-faq-heading`} className="text-2xl md:text-3xl font-serif font-bold text-slate-900 dark:text-white">
                        {title || t('faqSection.heading', 'Frequently asked questions')}
                    </h2>
                    {subtitle && (
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-2 leading-relaxed">{subtitle}</p>
                    )}
                </div>

                <div className="space-y-2.5">
                    {items.map((f, i) => {
                        const isOpen = open === i;
                        const panelId = `${uid}-faq-panel-${i}`;
                        return (
                            <div
                                key={i}
                                className="rounded-2xl border overflow-hidden transition-shadow hover:shadow-sm"
                                style={{ borderColor: 'var(--hm-card-border)', backgroundColor: 'var(--hm-card-bg)' }}
                            >
                                <h3 className="m-0">
                                    <button
                                        type="button"
                                        className="w-full flex items-start justify-between gap-4 p-4 sm:p-5 text-start"
                                        aria-expanded={isOpen}
                                        aria-controls={panelId}
                                        onClick={() => setOpen(isOpen ? null : i)}
                                    >
                                        <span className="font-bold text-slate-800 dark:text-white text-sm sm:text-base leading-snug">
                                            {f.q}
                                        </span>
                                        <span className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                                            isOpen ? 'bg-primary text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-500'
                                        }`}>
                                            <ChevronDown size={15} className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                                        </span>
                                    </button>
                                </h3>
                                {/* Answer stays in the DOM (hidden) rather than being
                                    unmounted, so on-page search (⌘F) and any crawler
                                    that reads rendered HTML still find the text. */}
                                <div
                                    id={panelId}
                                    hidden={!isOpen}
                                    className="px-4 sm:px-5 pb-4 sm:pb-5 text-sm text-slate-500 dark:text-slate-400 leading-relaxed border-t pt-3.5"
                                    style={{ borderColor: 'var(--hm-card-border)' }}
                                >
                                    {f.a}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default PageFaqSection;
